import AwesomeDebouncePromise from 'awesome-debounce-promise';

import { ServiceGroupQuery, ServiceGroupData } from 'chaika-frontend-types/frontend-cms';
import { ServiceGroupStatus } from 'chaika-enums';

import { store } from '@store';
import { ServiceGroupApi } from '@api/ServiceGroupApi';
import { getUniqNewIds } from '@utils/getUniqNewIds';
import { getUniqChunkedIds } from '@utils/getUniqChunkedIds';

const findApi = AwesomeDebouncePromise(ServiceGroupApi.getList, 500);

class ServiceGroupService {
    public async load(): Promise<void> {
        const serviceGroupList = store.serviceGroupStore.serviceGroupList;

        const { serviceGroups, count } = await ServiceGroupApi.getList(serviceGroupList.queryParams);

        serviceGroupList.set(serviceGroups);
        serviceGroupList.count = count;
    }

    public async loadNext(stopIndex = 0): Promise<void> {
        const serviceGroupList = store.serviceGroupStore.serviceGroupList;

        if (serviceGroupList.needLoadNext(stopIndex)) {
            const { serviceGroups } = await ServiceGroupApi.getList(serviceGroupList.queryParams);

            serviceGroupList.addNext(serviceGroups);
        }
    }

    public async loadByIds(ids: string[]): Promise<void> {
        const serviceGroupList = store.serviceGroupStore.serviceGroupList;

        const newIds = getUniqNewIds(serviceGroupList.ids, ids);

        if (newIds.length > 0) {
            const chunkIds = getUniqChunkedIds(newIds);

            const [...serviceGroupParams] = await Promise.all(chunkIds.map(ids => ServiceGroupApi.getList({ ids })));

            let newCount = 0;

            serviceGroupParams.forEach(({ serviceGroups, count }) => {
                serviceGroupList.add(serviceGroups);
                newCount = newCount + count;
            });

            serviceGroupList.count = serviceGroupList.count + newCount;
        }
    }

    public async find(params: ServiceGroupQuery): Promise<ServiceGroupData[]> {
        const { serviceGroups } = await ServiceGroupApi.getList(params);

        return serviceGroups;
    }

    public async loadSearch(): Promise<void> {
        store.serviceGroupStore.searchList.reset();

        const searchList = store.serviceGroupStore.searchList;
        const { serviceGroups, count } = await findApi(searchList.searchParams);

        searchList.setResult(serviceGroups, count);
    }

    public async updateSearchParams(params: ServiceGroupQuery, withoutParamsCheck?: boolean): Promise<void> {
        const searchList = store.serviceGroupStore.searchList;

        searchList.setFilterParams(params);

        const { serviceGroups, count } =
            searchList.searchParamsSet || withoutParamsCheck
                ? await findApi(searchList.searchParams)
                : { serviceGroups: [], count: 0 };

        searchList.setResult(serviceGroups, count);
    }

    public async loadNextSearch(): Promise<void> {
        const searchList = store.serviceGroupStore.searchList;
        const { limit = 50 } = store.serviceGroupStore.searchList.queryParams;

        if (searchList.needLoadNext(limit)) {
            const { serviceGroups } = await findApi(searchList.searchParams);

            searchList.addNext(serviceGroups);
        }
    }

    public async updateStatus(id: string, status: ServiceGroupStatus) {
        await ServiceGroupApi.updateStatus(id, status);

        const groupListItem = store.serviceGroupStore.serviceGroupList.get(id);
        const searchGroupListItem = store.serviceGroupStore.searchList.get(id);

        if (groupListItem) {
            store.serviceGroupStore.serviceGroupList.update(id, { ...groupListItem, status });
        }
        if (searchGroupListItem) {
            store.serviceGroupStore.searchList.update(id, { ...searchGroupListItem, status });
        }
    }
}

export const serviceGroupService = new ServiceGroupService();
